import { useSelector, useDispatch } from 'react-redux'
import React, { useState } from 'react'
import FormsyForm from 'formsy-react'
import PropTypes from 'prop-types'
import { format as formatDateTime, getDateTime } from '../../../services/date-time/index'
import { setOverlayDialogVisible } from '../../../services/dialog/actions'
import { selectStaticTextByKey } from '../../../services/static-text/selectors'
import {
  selectEmailAvailabilityProcessing,
  selectHasEmailAvailable,
  selectEmailAvailability,
  selectHasChildProfile,
  selectFirstName,
  selectEmail,
} from '../../../services/user/selectors'
import { GiftLoading } from '../GiftLoading/index'
import { H3, HEADING_TYPES } from '../../Heading/index'
import Button, { TYPES } from '../../Button/index'
import { TYPE_LOGIN } from '../../../services/dialog/index'
import { selectLocale } from '../../../services/page/index'

import {
  resetUserEmailAvailability,
  getUserEmailAvailability,
  resetUserChildAccount,
} from '../../../services/user/actions'

import {
  selectGiftOrderProcessing,
  selectRecipientEmail,
  selectDeliveryDate,
  selectGift,
} from '../../../services/gift/selectors'
import {
  GIFT_READY_STATE,
} from '../../../services/gift/index'
import {
  getMaxDeliveryDate,
} from '../../../services/gift/policies/index'

import {
  EmailInput,
  DateInput,
  TextInput,
  Textarea,
} from '../../FormInput.v2/index'

import {
  GIFT_FORM_STATIC_KEY as staticTextStoreKey,
} from './gift-form-static-key'

const GiftFormPropTypes = {
  toggleEmailFields: PropTypes.func,
  sendByEmail: PropTypes.bool,
  editing: PropTypes.bool,
  submit: PropTypes.func,
}

const invalidName = 'Friend of Gaia'

function getHeaderText (loggedIn, canPurchaseGift, staticText) {
  if (loggedIn && (canPurchaseGift === GIFT_READY_STATE.CANNOT_PURCHASE)) return staticText.get('cannotPurchaseHeader')
  if (loggedIn && (canPurchaseGift === GIFT_READY_STATE.CAN_PURCHASE)) return staticText.get('yourInfo')
  return null
}

export default function GiftForm (props) {
  const staticText = useSelector(selectStaticTextByKey(staticTextStoreKey))
  const orderProcessing = useSelector(selectGiftOrderProcessing)
  const firstName = useSelector(selectFirstName)
  const locale = useSelector(selectLocale)
  const email = useSelector(selectEmail)
  const gift = useSelector(selectGift)
  const dispatch = useDispatch()

  const {
    toggleEmailFields,
    completePurchase,
    canPurchaseGift,
    toggleEditing,
    sendByEmail,
    reviewing,
    loggedIn,
    editing,
    submit,
  } = props

  const today = formatDateTime(new Date(), locale, 'YYYY-MM-DD')
  const intitialGifterName = firstName !== invalidName ? firstName : ''
  const gifterName = gift.getIn(['details', 'gifterName'], '') !== '' ? gift.getIn(['details', 'gifterName'], '') : intitialGifterName
  const recipientEmail = useSelector(selectRecipientEmail)
  const unformattedDeliveryDate = useSelector((state) => selectDeliveryDate(state, today))
  const emailAvailability = useSelector(selectEmailAvailability)
  const emailAvailabilityProcessing = useSelector(selectEmailAvailabilityProcessing)
  const hasEmailAvailable = useSelector(selectHasEmailAvailable)
  const hasChildProfile = useSelector(selectHasChildProfile)
  const [emailValue, setEmailValue] = useState(recipientEmail || '')
  const [formValid, setFormValid] = useState(false)
  const [onBlurEmailValue, setOnBlurEmailValue] = useState(null)
  const deliveryDate = unformattedDeliveryDate
    // getDateTime to standardize incoming format
    ? formatDateTime(getDateTime(unformattedDeliveryDate), locale, 'YYYY-MM-DD')
    : null

  const showEmailFields = sendByEmail || (recipientEmail && deliveryDate)
  const recipientFirstName = gift.getIn(['details', 'recipientFirstName'], '')
  const recipientLastName = gift.getIn(['details', 'recipientLastName'], '')
  const message = gift.getIn(['details', 'message'], '') || ''
  const showMessage = editing || (!editing && message)
  const showWithOrWithoutEmailBtns = editing && !sendByEmail && !reviewing
  const showNextOrBackBtns = editing && !reviewing && sendByEmail
  const showCompleteOrEditBtns = !editing && reviewing
  const showConfirmOrCancel = editing && reviewing

  const disabled = canPurchaseGift === GIFT_READY_STATE.NOT_READY
    || canPurchaseGift === GIFT_READY_STATE.CANNOT_PURCHASE
  const readonly = !editing && !disabled
  const sectionTopClass = loggedIn && (canPurchaseGift === GIFT_READY_STATE.CANNOT_PURCHASE)
    ? ' gift-form__top--red'
    : ''

  const getForcedEmailError = () => {
    let forcedError = ''
    if (hasEmailAvailable && (emailAvailability === null)) {
      forcedError = 'isEmailCaptureValid'
    }
    return forcedError
  }

  function handleOnChangeEmail (value) {
    setEmailValue(value)
    if (hasEmailAvailable) {
      dispatch(resetUserEmailAvailability())
    }
    if (hasChildProfile) {
      dispatch(resetUserChildAccount())
    }
  }

  function handleOnBlurCheckEmail (value) {
    const pattern = /^.+@.+\..{2,}$/
    // We are compensating for Formsy saying the email is valid (when its not)
    // and if the email value equals what has already been checked, then bail
    if (!pattern.test(value) || value === onBlurEmailValue) {
      return
    }
    dispatch(getUserEmailAvailability(value))
    setOnBlurEmailValue(value)
  }

  return (
    <>
      <FormsyForm
        onValid={() => setFormValid(true)}
        onInvalid={() => setFormValid(false)}
        className="gift-form"
        onValidSubmit={submit}
      >
        <div className={`gift-form__top ${sectionTopClass}`}>
          <H3
            as={[HEADING_TYPES.H6_MOBILE, HEADING_TYPES.H5_DESKTOP]}
            className="gift-form__subtitle"
          >
            {getHeaderText(loggedIn, canPurchaseGift, staticText)}
          </H3>
          {!loggedIn && (
            <React.Fragment>
              <p className="gift-form__text">
                {staticText.get('pleaseLogin')}
              </p>
              <Button
                onClick={() => dispatch(setOverlayDialogVisible(TYPE_LOGIN))}
                className="gift-form__btn"
                type={TYPES.PRIMARY}
              >{staticText.get('login')}
              </Button>
            </React.Fragment>
          )}
          {loggedIn && (canPurchaseGift === GIFT_READY_STATE.CANNOT_PURCHASE) && (
            <p className="gift-form__text">
              {staticText.get('cannotPurchaseBody')}
            </p>
          )}
          {loggedIn && (canPurchaseGift === GIFT_READY_STATE.CAN_PURCHASE) && (
            <React.Fragment>
              <div className="gift-form__content">
                <TextInput
                  name="gifterName"
                  label={staticText.get('firstName')}
                  value={gifterName}
                  disabled={disabled}
                  readonly={readonly}
                  required={editing}
                  maxLength={32}
                  tabIndex="1"
                />
                <EmailInput
                  label={staticText.get('email')}
                  disabled={disabled}
                  required={editing}
                  name="gifterEmail"
                  maxLength={32}
                  value={email}
                  tabIndex="2"
                  readonly
                />
              </div>
            </React.Fragment>
          )}
        </div>
        <div className="gift-form__lower">
          <H3
            as={[HEADING_TYPES.H6_MOBILE, HEADING_TYPES.H5_DESKTOP]}
            className="gift-form__subtitle"
          >
            {staticText.get('recipientInfo')}
          </H3>
          <div>
            <TextInput
              label={staticText.get('recipientFirstName')}
              value={recipientFirstName}
              name="recipientFirstName"
              disabled={disabled}
              readonly={readonly}
              required={editing}
              maxLength={32}
              tabIndex="3"
            />
            <TextInput
              label={staticText.get('recipientLastName')}
              value={recipientLastName}
              name="recipientLastName"
              disabled={disabled}
              readonly={readonly}
              required={editing}
              maxLength={32}
              tabIndex="3"
            />
            {showEmailFields && reviewing && (
              <>
                <EmailInput
                  label={staticText.get('recipientEmail')}
                  className="gift-form__recipient-email"
                  value={recipientEmail}
                  name="recipientEmail"
                  disabled={disabled}
                  readonly={readonly}
                  required={editing}
                  maxLength={32}
                  tabIndex="5"
                />
                <DateInput
                  label={staticText.get('deliveryDate')}
                  max={getMaxDeliveryDate(today)}
                  value={deliveryDate || today}
                  readonly={readonly}
                  disabled={disabled}
                  name="deliveryDate"
                  required={editing}
                  min={today}
                  tabIndex="6"
                />
              </>
            )}
          </div>
          {showMessage && (
            <React.Fragment>
              <H3
                as={[HEADING_TYPES.H6_MOBILE, HEADING_TYPES.H5_DESKTOP]}
                className="gift-form__subtitle"
              >{staticText.get('message')}
              </H3>
              <Textarea
                label={staticText.get('message')}
                validations="maxLength:500"
                showCharCount={editing}
                readonly={readonly}
                disabled={disabled}
                value={message}
                name="message"
                tabIndex="4"
                block
              />
            </React.Fragment>
          )}
          {sendByEmail || reviewing ? null : (
            <p className="gift-form__text">
              {staticText.get('ifYouKnow')}
            </p>
          )}
          {showEmailFields && !reviewing ? (
            <React.Fragment>
              <H3
                as={[HEADING_TYPES.H6_MOBILE, HEADING_TYPES.H5_DESKTOP]}
                className="gift-form__subtitle"
              >{staticText.get('recipientEmail')}
              </H3>
              <EmailInput
                validationErrors={{
                  isEmail: staticText.get('emailValidError'),
                  isEmailAvailable: staticText.get('emailAvailableError'),
                  isEmailCaptureValid: staticText.get('errorMessageInvalidEmail'),
                }}
                className="gift-form__email"
                disabled={disabled || emailAvailabilityProcessing}
                validations={{
                  isEmail: true,
                }}
                label={staticText.get('recipientEmail')}
                forceError={getForcedEmailError()}
                onBlur={handleOnBlurCheckEmail}
                onChange={handleOnChangeEmail}
                validationTimeout={750}
                name="recipientEmail"
                readonly={readonly}
                required={editing}
                value={emailValue}
                maxLength={32}
                tabIndex="5"
              />
              {sendByEmail && (
                <p className="gift-form__date-copy">
                  {staticText.get('whichDay')}
                </p>
              )}
              <DateInput
                label={staticText.get('deliveryDate')}
                max={getMaxDeliveryDate(today)}
                value={deliveryDate || today}
                readonly={readonly}
                disabled={disabled}
                name="deliveryDate"
                required={editing}
                min={today}
                tabIndex="6"
              />
            </React.Fragment>
          ) : null}
          <GiftLoading
            visible={orderProcessing}
            loadingText={staticText.get('pleaseWait')}
          />
          <div className="gift-form__btns">
            {showWithOrWithoutEmailBtns ? (
              <React.Fragment>
                <Button
                  className="gift-form__btn"
                  type={TYPES.SECONDARY}
                  disabled={disabled || !formValid}
                  submit
                >{staticText.get('giveWithoutEmail')}
                </Button>
                <Button
                  onClick={toggleEmailFields}
                  className="gift-form__btn"
                  type={TYPES.PRIMARY}
                  disabled={disabled}
                >{staticText.get('sendByEmail')}
                </Button>
              </React.Fragment>
            ) : null}
            {showNextOrBackBtns ? (
              <React.Fragment>
                <Button
                  onClick={toggleEmailFields}
                  className="gift-form__btn"
                  type={TYPES.SECONDARY}
                  disabled={disabled}
                >{staticText.get('back')}
                </Button>
                <Button
                  disabled={
                    !formValid
                    || disabled
                    || (showEmailFields && (!!getForcedEmailError() || !hasEmailAvailable))
                  }
                  className="gift-form__btn"
                  type={TYPES.PRIMARY}
                  submit
                >{staticText.get('next')}
                </Button>
              </React.Fragment>
            ) : null}
            {showCompleteOrEditBtns ? (
              <React.Fragment>
                <Button
                  disabled={disabled || orderProcessing}
                  className="gift-form__btn"
                  onClick={toggleEditing}
                  type={TYPES.SECONDARY}
                >{staticText.get('edit')}
                </Button>
                <Button
                  disabled={disabled || orderProcessing}
                  onClick={completePurchase}
                  className="gift-form__btn"
                  type={TYPES.PRIMARY}
                >
                  {orderProcessing
                    ? staticText.get('processing', '')
                    : staticText.get('complete')}
                </Button>
              </React.Fragment>
            ) : null}
            {showConfirmOrCancel ? (
              <React.Fragment>
                <Button
                  className="gift-form__btn"
                  type={TYPES.SECONDARY}
                  disabled={disabled}
                  submit
                >{staticText.get('confirm')}
                </Button>
                <Button
                  onClick={toggleEditing}
                  className="gift-form__btn"
                  disabled={disabled}
                  type={TYPES.PRIMARY}
                >{staticText.get('cancel')}
                </Button>
              </React.Fragment>
            ) : null}
          </div>
        </div>
      </FormsyForm>
    </>
  )
}

GiftForm.propTypes = GiftFormPropTypes
